import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield, Heart, Sparkles, BookOpen, Users, Globe, Lock, Award } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { useLang } from "@/hooks/use-language";

export const Route = createFileRoute("/why")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/why" },
      { title: "Why Jozori? | لماذا جذوري؟ — Jozori" },
      {
        name: "description",
        content:
          "Why families choose Jozori: a warm, bilingual, ad-free world where children grow in values, feelings, and character — built with Arab parents, educators, and child psychologists.",
      },

      { property: "og:title", content: "Why Jozori? | لماذا جذوري؟" },
      {
        property: "og:description",
        content:
          "Eight reasons parents trust Jozori with their children — and the language and identity of their family.",
      },
      { property: "og:image", content: "https://jozori.com/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://jozori.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/why" }],
  }),
  component: WhyPage,
});

const COPY = {
  en: {
    eyebrow: "Why Jozori?",
    title: "Eight reasons parents trust us with their children's roots.",
    lede: "We built Jozori with the same care we'd want for our own kids — because most of us are raising ours alongside yours.",
    reasons: [
      {
        icon: Heart,
        t: "Built by Arab parents",
        d: "Every decision is made by people raising bilingual kids abroad. We've lived the problem we're solving.",
      },
      {
        icon: BookOpen,
        t: "Truly bilingual, never translated",
        d: "Arabic content is written in Arabic from day one — not translated from English. Authentic voice, every time.",
      },
      {
        icon: Shield,
        t: "Zero ads, ever",
        d: "No banner ads, no autoplay, no third-party trackers. What your child sees is what we crafted for them.",
      },
      {
        icon: Users,
        t: "Designed for the whole family",
        d: "Parents get a real dashboard, not a settings tab. Co-parents can join with full visibility, no extra fees.",
      },
      {
        icon: Sparkles,
        t: "Reviewed by educators & psychologists",
        d: "Every story, video, and activity passes through educators and child psychologists who care about how children actually feel and grow.",
      },

      {
        icon: Award,
        t: "Values-driven content",
        d: "Stories anchored in honesty, kindness, family, and identity — without preaching, without dilution.",
      },
      {
        icon: Globe,
        t: "Made for the diaspora",
        d: "We design for kids in London, Toronto, Sydney, and Dubai — built to feel like home from anywhere on earth.",
      },
      {
        icon: Lock,
        t: "Privacy you can trust",
        d: "COPPA-aligned safety, no behavioral profiling, no data sold. Ever. Your family is not the product.",
      },
    ],
    cta: "Learn how Jozori keeps your kids safe",
  },
  ar: {
    eyebrow: "لماذا جذوري؟",
    title: "ثمانية أسباب تجعل الأهل يأتمنوننا على جذور أطفالهم.",
    lede: "بنينا «جذوري» بنفس العناية التي نرغبها لأطفالنا نحن — لأن معظمنا يربّي أطفاله إلى جانب أطفالكم.",
    reasons: [
      {
        icon: Heart,
        t: "صُنع بأيدي آباء عرب",
        d: "كل قرار نتّخذه يصنعه أهلٌ يربّون أطفالًا ثنائيي اللغة في الغربة. عشنا المشكلة قبل أن نحلّها.",
      },
      {
        icon: BookOpen,
        t: "عربية دافئة تشبه البيت",
        d: "كلمات عربية قريبة من قلب الطفل، تُكتب بصوتنا — لا تُترجم عن لغة أخرى.",
      },
      {
        icon: Shield,
        t: "بلا إعلانات إطلاقًا",
        d: "لا إعلانات، لا تشغيل تلقائي، لا متتبّعات خارجية. ما يراه طفلك هو فقط ما صمّمناه له.",
      },
      {
        icon: Users,
        t: "مصمَّم للعائلة كاملة",
        d: "للأهل لوحة تحكّم حقيقية لا مجرد إعدادات. ويمكن للوالد الآخر الانضمام بكل وضوح ودون رسوم إضافية.",
      },
      {
        icon: Sparkles,
        t: "يُصنع بعناية تناسب عالم الطفل",
        d: "كل قصة وحوار ونشاط يُبنى بهدوء، مع مختصّين في عالم الطفولة، ليصل إلى قلب طفلك كما يستحق.",
      },
      {
        icon: Award,
        t: "محتوى يحمل قيمنا",
        d: "قصص جذورها في الصدق، اللطف، العائلة، والهوية — بلا وعظ، وبلا تخفيف.",
      },
      {
        icon: Globe,
        t: "للمهجر، بعين المهجر",
        d: "نُصمّم لأطفال لندن وتورنتو وسيدني ودبي — ليشعر طفلك أنه في بيته، من أي مكان في العالم.",
      },
      {
        icon: Lock,
        t: "خصوصية تستحق ثقتك",
        d: "حماية متوافقة مع COPPA، بلا ملفّات سلوكية، وبلا بيع للبيانات أبدًا. عائلتك ليست سلعة.",
      },
    ],
    cta: "اكتشف كيف نحمي أطفالك",
  },
} as const;

function WhyPage() {
  const { lang } = useLang();
  const t = COPY[lang];
  const isAr = lang === "ar";
  return (
    <SiteShell active="/why">
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-secondary/15 via-transparent to-primary/10"
        />
        <div className="relative mx-auto max-w-3xl px-4 py-20 md:py-28 text-center">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary">
            {t.eyebrow}
          </p>
          <h1
            className={`text-4xl md:text-6xl font-black leading-tight mb-6 ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {t.title}
          </h1>
          <p className="text-lg text-foreground/70 leading-relaxed">{t.lede}</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {t.reasons.map((r) => {
          const Icon = r.icon;
          return (
            <article
              key={r.t}
              className={`rounded-2xl border border-clay/40 bg-card p-6 hover:shadow-md transition ${
                isAr ? "text-right" : ""
              }`}
            >
              <div className="mb-4 inline-flex size-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <Icon className="size-6" />
              </div>
              <h3 className="font-black text-lg mb-2">{r.t}</h3>
              <p className="text-sm text-foreground/70 leading-relaxed">{r.d}</p>
            </article>
          );
        })}
      </section>

      <div className="text-center pb-20 pt-6">
        <Link
          to="/safety"
          className="inline-flex items-center rounded-full bg-primary px-7 py-3.5 font-black text-primary-foreground shadow hover:opacity-90"
        >
          {t.cta}
        </Link>
      </div>
    </SiteShell>
  );
}
